import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx } from "react/jsx-runtime";
import { useHistory } from 'react-router';
import { ConfirmationModal, useOverlayContext } from 'seagull';
import { getTestIds } from 'seagull/utils/TestIds';
import { ErrorText, routes } from '~/constants';
import { useStoreActions } from '~/contexts';
import { useDraftProposal } from '~/hooks/reactQuery/mutations/useDraftProposal';
import { useMutationState } from '~/hooks/reactQuery/useMutationState';
import { useDuplicateRelationship } from '~/hooks/useDuplicateRelationship';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { ErrorResponse, ServerError } from '~/infra/ErrorResponse';
export const WithdrawToEditProposalTestIds = getTestIds('WithdrawToEditProposalTestIds');
export function useWithdrawToEditProposal() {
    const overlayContext = useOverlayContext();
    const { duplicateRelationship } = useDuplicateRelationship();
    const { api } = useStoreActions();
    const history = useHistory();
    const { EDIT_PROPOSAL_IN_PLACE } = useFeatureFlags();
    const { t } = useLocaleKeys('agreementStudio.editInPlace');
    const { draftProposal } = useDraftProposal({
        onSuccess: (newDraftId)=>history.push(routes.draft(newDraftId))
    });
    const { mutateAsync: withdrawForEditing } = useMutationState({
        mutationFn: async (param)=>{
            let { id } = param;
            await api.relationship.withdrawForEditing(id);
        },
        withPageLoader: true,
        mutationName: 'withdraw-for-editing',
        toasts: {
            errorMessage: t.withdrawToEditErrorToast(),
            errorTestId: WithdrawToEditProposalTestIds.ErrorToast()
        }
    });
    async function showWithdrawConfirmation(action, modalProps) {
        const confirmed = await overlayContext.showModal({
            closeOnClickOutside: true,
            shouldBlurBackground: true,
            closeOnEscapeKey: true,
            content: (param)=>{
                let { dismissModal } = param;
                return /*#__PURE__*/ _jsx(ConfirmationModal, _object_spread_props(_object_spread({
                    skin: "danger"
                }, modalProps), {
                    onPrimaryButtonClick: ()=>dismissModal(true),
                    onSecondaryButtonClick: ()=>dismissModal(false)
                }));
            }
        });
        if (confirmed) {
            try {
                await action();
            } catch (unused) {
            // errors handled by useMutationState onError
            }
        }
    }
    async function cancelProposal(id) {
        try {
            await api.relationship.cancelProposal(id);
        } catch (err) {
            if (new ErrorResponse(err).hasServerError(ServerError.InvalidRelationshipStatus)) {
                overlayContext.showErrorToast(ErrorText.ProposalAlreadyAcceptedOrCanceled);
            } else {
                overlayContext.showErrorToast(ErrorText.UnexpectedErrorTryAgain, 'cancel-proposal-error');
            }
            throw err;
        }
    }
    return {
        withdrawToEditProposalByIdWithConfirmation: async (id, draftId)=>{
            if (EDIT_PROPOSAL_IN_PLACE && draftId) {
                return showWithdrawConfirmation(async ()=>{
                    await withdrawForEditing({
                        id
                    });
                    history.push(routes.draft(draftId));
                }, {
                    title: t.withdrawToEditConfirmationTitle(),
                    content: t.withdrawToEditConfirmationContent(),
                    primaryButtonLabel: t.withdrawToEditConfirmationPrimaryButton(),
                    secondaryButtonLabel: t.withdrawToEditSecondaryButton()
                });
            }
            return showWithdrawConfirmation(()=>{
                draftProposal({
                    relationshipId: id,
                    draftId
                });
            }, {
                title: t.withdrawToEditLegacyConfirmationTitle(),
                content: t.withdrawToEditLegacyConfirmationContent(),
                primaryButtonLabel: t.withdrawToEditLegacyConfirmationPrimaryButton(),
                secondaryButtonLabel: t.withdrawToEditSecondaryButton()
            });
        },
        withdrawToEditProposalById: async function withdrawToEditProposalById(id) {
            let opts = arguments.length > 1 && arguments[1] !== void 0 ? arguments[1] : {};
            if (EDIT_PROPOSAL_IN_PLACE && opts.draftId) {
                await withdrawForEditing({
                    id
                });
                return opts.draftId;
            }
            const draftId = await duplicateRelationship(id, {
                strategy: 'withdraw-to-edit',
                onError: opts.onDuplicateRelationshipError
            });
            await cancelProposal(id);
            return draftId;
        }
    };
}
